import { Button as AntButton, Select } from "antd"
import { useEffect, useMemo, useState } from "react"
import { Button as BootstrapButton, Col, Form, Row } from "react-bootstrap"
import { DollarInputV2 } from "./DollarInputV2"
import CheckGroup from "../Common/components/CheckGroup"
import { Tips } from "../Common/Tooltip"
import * as com from "../Common"
import "./styles.css"

const FieldError = ({ message }) => (
    message ? <div className="warning-msg">{message}</div> : null
)

const scrollToField = (fieldKey) => {
    if (!fieldKey || typeof document === "undefined") return
    const el = document.querySelector(`[data-field="${fieldKey}"]`)
    if (el && typeof el.scrollIntoView === "function") {
        el.scrollIntoView({ behavior: "smooth", block: "center" })
    }
}

const emptyAssets = {
    accounts: [],
    gifts: [],
    otherAssets: [],
}

const newKey = () => `${Date.now()}-${Math.random()}`

const otherAssetType = (value) => com.otherassetsandcredits.find((item) => item[0] === value)
const giftSourceInfo = (value) => com.fundssourcetype.find((item) => item[0] === value)

const normalizeOwnershipMode = (ownership, ownerPartyIds = []) => {
    const raw = `${ownership || ""}`.trim().toLowerCase()
    if (raw === "joint" || (Array.isArray(ownerPartyIds) && ownerPartyIds.length > 1)) {
        return "Joint"
    }
    return "Individual"
}

const participantDisplayName = (participant) => {
    const name = [participant?.firstName, participant?.middleName, participant?.lastName]
        .map((part) => `${part || ""}`.trim())
        .filter(Boolean)
        .join(" ")
    return name || "Applicant"
}

const normalizeAssetItem = (item = {}, currentPartyId = "") => {
    const ownerPartyIds = Array.isArray(item.ownerPartyIds)
        ? item.ownerPartyIds.filter(Boolean)
        : []
    const ownership = normalizeOwnershipMode(item.ownership, ownerPartyIds)
    const resolvedOwners = ownership === "Joint"
        ? Array.from(new Set([currentPartyId, ...ownerPartyIds].filter(Boolean)))
        : [currentPartyId].filter(Boolean)
    return {
        ...item,
        ownership,
        ownerPartyIds: resolvedOwners.length > 0 ? resolvedOwners : (currentPartyId ? [currentPartyId] : []),
    }
}

const normalizeAssets = (assets = {}, currentPartyId = "") => ({
    accounts: Array.isArray(assets.accounts)
        ? assets.accounts.map((item) => normalizeAssetItem(item, currentPartyId))
        : [],
    gifts: Array.isArray(assets.gifts)
        ? assets.gifts.map((item) => normalizeAssetItem(item, currentPartyId))
        : [],
    otherAssets: Array.isArray(assets.otherAssets)
        ? assets.otherAssets.map((item) => normalizeAssetItem(item, currentPartyId))
        : [],
})

const updateListItem = (items, index, patch) => (
    items.map((item, itemIndex) => (itemIndex === index ? { ...item, ...patch } : item))
)

const removeListItem = (items, index) => items.filter((_, itemIndex) => itemIndex !== index)

const hasAmount = (value) => {
    if (value === "" || value == null) return false
    const parsed = Number(value)
    return !Number.isNaN(parsed)
}

const jointOthersSelected = (item, currentPartyId) => (
    (item.ownerPartyIds || []).filter((id) => id && id !== currentPartyId)
)

const addError = (errors, order, key, message) => {
    if (errors[key]) return
    errors[key] = message
    order.push(key)
}

const YesNoQuestion = ({ label, help, value, onChange }) => (
    <Row className="ful-width-wrap">
        <Col className="mt-4" md={12} xs={12}>
            <Form.Group className="inputSection finance-v2-yesno">
                <Form.Label className="finance-v2-yesno-label">
                    {label}
                    {help}
                </Form.Label>
                <CheckGroup
                    data={[{ value: "true", label: "Yes" }, { value: "false", label: "No" }]}
                    onChangeSelect={(selected) => onChange(selected === "true")}
                    value={value === true ? "true" : value === false ? "false" : ""}
                    type="radio"
                    direction="horizontal"
                />
            </Form.Group>
        </Col>
    </Row>
)

const OwnershipSelect = ({
    currentPartyId,
    ownership,
    ownerPartyIds,
    onChange,
}) => {
    const selectedOthers = jointOthersSelected({ ownerPartyIds }, currentPartyId)
    const isJoint = ownership === "Joint"

    return (
        <Col className="mt-4">
            <Form.Group className="text-left">
                <Form.Label className="text-left text-nowrap">Ownership</Form.Label>
                <Form.Control
                    as="select"
                    value={isJoint ? "Joint" : "Individual"}
                    onChange={(e) => {
                        if (e.target.value === "Joint") {
                            onChange({
                                ownership: "Joint",
                                ownerPartyIds: [currentPartyId, ...selectedOthers].filter(Boolean),
                            })
                            return
                        }
                        onChange({
                            ownership: "Individual",
                            ownerPartyIds: currentPartyId ? [currentPartyId] : [],
                        })
                    }}
                >
                    <option value="Individual">Individual</option>
                    <option value="Joint">Joint</option>
                </Form.Control>
            </Form.Group>
        </Col>
    )
}

const SharedWithRow = ({
    currentPartyId,
    ownership,
    ownerPartyIds,
    otherParticipants,
    error,
    fieldKey,
    onChange,
}) => {
    if (ownership !== "Joint") {
        return null
    }
    const selectedOthers = jointOthersSelected({ ownerPartyIds }, currentPartyId)

    return (
        <Form.Row className="finances-type-mobile">
            <Col className="mt-4" md={6} xs={12} data-field={fieldKey}>
                <Form.Group className="text-left">
                    <Form.Label className="text-left text-nowrap">*Shared with</Form.Label>
                    <Select
                        mode="multiple"
                        allowClear
                        showSearch
                        optionFilterProp="label"
                        placeholder="Select applicants"
                        style={{ width: "100%", minHeight: 38 }}
                        value={selectedOthers}
                        options={otherParticipants.map((person) => ({
                            value: person.partyId,
                            label: participantDisplayName(person),
                        }))}
                        status={error ? "error" : undefined}
                        onChange={(ids) => onChange({
                            ownership: "Joint",
                            ownerPartyIds: [currentPartyId, ...(ids || [])].filter(Boolean),
                        })}
                    />
                    <FieldError message={error} />
                </Form.Group>
            </Col>
        </Form.Row>
    )
}

const GiftSourceLabel = ({ source }) => {
    const info = giftSourceInfo(source)
    const help = info?.[2] || ""
    if (!help || source === "Other") {
        return <>*Source</>
    }
    return (
        <>
            *Source
            <Tips content={help} placement="bottom" title="Source" />
        </>
    )
}

const OtherAssetLabel = ({ assetType }) => {
    if (assetType === "Other") {
        return "*Asset or Credit Type"
    }
    const help = otherAssetType(assetType)?.[2] || ""
    if (!help) {
        return "*Asset or Credit Type"
    }
    return (
        <>
            *Asset or Credit Type
            <Tips content={help} placement="bottom" title="Asset or Credit Type" />
        </>
    )
}

const AssetsV2 = ({ application, assets: initialAssets, participant, saving, onSave, onBack }) => {
    const [assets, setAssets] = useState(emptyAssets)
    const [fieldErrors, setFieldErrors] = useState({})
    const currentPartyId = participant?.partyId || ""

    useEffect(() => {
        setAssets(normalizeAssets(initialAssets, currentPartyId))
        setFieldErrors({})
    }, [initialAssets, currentPartyId])

    const clearFieldError = (key) => {
        setFieldErrors((prev) => {
            if (!prev[key]) return prev
            const next = { ...prev }
            delete next[key]
            return next
        })
    }

    const clearKeysForPatch = (listKey, index, patch) => {
        setFieldErrors((prev) => {
            let changed = false
            const next = { ...prev }
            Object.keys(patch).forEach((field) => {
                if (field === "ownership" || field === "ownerPartyIds") {
                    const sharedKey = `${listKey}.${index}.sharedWith`
                    if (next[sharedKey]) {
                        delete next[sharedKey]
                        changed = true
                    }
                    return
                }
                const key = `${listKey}.${index}.${field}`
                if (next[key]) {
                    delete next[key]
                    changed = true
                }
            })
            return changed ? next : prev
        })
    }

    const setList = (key, updater) => {
        setAssets((prev) => ({
            ...prev,
            [key]: typeof updater === "function" ? updater(prev[key]) : updater,
        }))
    }

    const patchItem = (listKey, index, patch) => {
        setList(listKey, (items) => updateListItem(items, index, patch))
        clearKeysForPatch(listKey, index, patch)
    }

    const showOwnership = (application?.participantOrder || []).length > 1
    const otherParticipants = useMemo(() => (
        (application?.participantOrder || [])
            .filter((id) => id && id !== currentPartyId)
            .map((id) => application?.participantsById?.[id])
            .filter(Boolean)
    ), [application, currentPartyId])

    const defaultOwnershipPatch = () => ({
        ownership: "Individual",
        ownerPartyIds: currentPartyId ? [currentPartyId] : [],
    })

    const addAccount = () => {
        setFieldErrors({})
        setList("accounts", (items) => [...items, {
            key: newKey(),
            bankName: "",
            account: "",
            accountType: "CheckingAccount",
            balance: "",
            ...defaultOwnershipPatch(),
        }])
    }

    const addGift = () => {
        setFieldErrors({})
        setList("gifts", (items) => [...items, {
            key: newKey(),
            assetType: "GiftOfCash",
            value: "",
            source: "",
            sourceDescription: "",
            deposited: false,
            ...defaultOwnershipPatch(),
        }])
    }

    const addOtherAsset = () => {
        setFieldErrors({})
        setList("otherAssets", (items) => [...items, {
            key: newKey(),
            description: "",
            assetType: "ProceedsFromRealEstatePropertyToBeSoldOnOrBeforeClosing",
            value: "",
            ...defaultOwnershipPatch(),
        }])
    }

    // Same rules as before — collect all field errors instead of first-only alert.
    const validateAssets = () => {
        const errors = {}
        const order = []

        for (let i = 0; i < assets.accounts.length; i += 1) {
            const item = assets.accounts[i]
            if (!`${item.bankName || ""}`.trim()) {
                addError(errors, order, `accounts.${i}.bankName`, `Please provide the name of the bank/credit union for account #${i + 1}.`)
            }
            if (!`${item.account || ""}`.trim()) {
                addError(errors, order, `accounts.${i}.account`, `Please provide account number for account #${i + 1}.`)
            }
            if (!hasAmount(item.balance)) {
                addError(errors, order, `accounts.${i}.balance`, `Please provide account balance for account #${i + 1}.`)
            }
            if (showOwnership && item.ownership === "Joint" && jointOthersSelected(item, currentPartyId).length === 0) {
                addError(errors, order, `accounts.${i}.sharedWith`, `Please select at least one other applicant to share account #${i + 1}.`)
            }
        }
        for (let i = 0; i < assets.gifts.length; i += 1) {
            const item = assets.gifts[i]
            if (!item.assetType) {
                addError(errors, order, `gifts.${i}.assetType`, `Please select an asset type for gift #${i + 1}.`)
            }
            if (item.deposited !== true && item.deposited !== false) {
                addError(errors, order, `gifts.${i}.deposited`, `Please select whether gift #${i + 1} is deposited.`)
            }
            if (!item.source) {
                addError(errors, order, `gifts.${i}.source`, `Please select a source for gift #${i + 1}.`)
            }
            if (!hasAmount(item.value) || Number(item.value) < 1) {
                addError(errors, order, `gifts.${i}.value`, `Please provide gift value for gift #${i + 1}.`)
            }
            if (showOwnership && item.ownership === "Joint" && jointOthersSelected(item, currentPartyId).length === 0) {
                addError(errors, order, `gifts.${i}.sharedWith`, `Please select at least one other applicant to share gift #${i + 1}.`)
            }
        }
        for (let i = 0; i < assets.otherAssets.length; i += 1) {
            const item = assets.otherAssets[i]
            if (!item.assetType) {
                addError(errors, order, `otherAssets.${i}.assetType`, `Please select an asset type for other asset #${i + 1}.`)
            }
            if (item.assetType === "Other" && !`${item.description || ""}`.trim()) {
                addError(errors, order, `otherAssets.${i}.description`, `Please describe other asset #${i + 1}.`)
            }
            if (!hasAmount(item.value) || Number(item.value) < 1) {
                addError(errors, order, `otherAssets.${i}.value`, `Please provide value for other asset #${i + 1}.`)
            }
            if (showOwnership && item.ownership === "Joint" && jointOthersSelected(item, currentPartyId).length === 0) {
                addError(errors, order, `otherAssets.${i}.sharedWith`, `Please select at least one other applicant to share other asset #${i + 1}.`)
            }
        }

        return { errors, firstErrorKey: order[0] || null }
    }

    const handleNext = () => {
        const { errors, firstErrorKey } = validateAssets()
        if (firstErrorKey) {
            setFieldErrors(errors)
            setTimeout(() => scrollToField(firstErrorKey), 50)
            return
        }
        setFieldErrors({})
        onSave(normalizeAssets(assets, currentPartyId))
    }

    const ownershipOnChange = (listKey, index) => (patch) => patchItem(listKey, index, patch)

    const ownershipSelect = (item, listKey, index) => (
        showOwnership ? (
            <OwnershipSelect
                currentPartyId={currentPartyId}
                ownership={item.ownership}
                ownerPartyIds={item.ownerPartyIds}
                onChange={ownershipOnChange(listKey, index)}
            />
        ) : null
    )

    const sharedWithRow = (item, listKey, index) => (
        showOwnership ? (
            <SharedWithRow
                currentPartyId={currentPartyId}
                ownership={item.ownership}
                ownerPartyIds={item.ownerPartyIds}
                otherParticipants={otherParticipants}
                fieldKey={`${listKey}.${index}.sharedWith`}
                error={fieldErrors[`${listKey}.${index}.sharedWith`]}
                onChange={ownershipOnChange(listKey, index)}
            />
        ) : null
    )

    return (
        <>
        <div className="text-left income-application newInterviewPaneProperty application-v2-form-body finance-v2-body">
            <div className="page-header page-header-row">
                <span className="page-header-primary">Finance</span>
                <span className="page-header-separator">/</span>
                <span className="page-header-secondary">Assets</span>
            </div>

            <YesNoQuestion
                label="Would you like to add any financial accounts to your application?"
                help={(
                    <Tips
                        content={<div>This is only necessary for <b>asset depletion</b> mortgages, and to ensure you have enough reserves for certain loans.</div>}
                        placement="bottom"
                        title="Asset depletion only"
                    />
                )}
                value={assets.accounts.length > 0}
                onChange={(value) => {
                    setFieldErrors({})
                    if (value) {
                        if (assets.accounts.length === 0) addAccount()
                        return
                    }
                    setList("accounts", [])
                }}
            />
            {assets.accounts.map((item, index) => (
                <div className="finances-type incard mt-4 pt-0 finance-v2-card" style={{ padding: "1rem" }} key={item.key || index}>
                    <Row style={{ position: "relative" }}>
                        <Col>
                            <Form.Row className="finances-type-mobile">
                                <Col className="mt-4" data-field={`accounts.${index}.bankName`}>
                                    <Form.Group className="text-left">
                                        <Form.Label className="text-left text-nowrap">*Institution</Form.Label>
                                        <Form.Control
                                            value={item.bankName || ""}
                                            isInvalid={!!fieldErrors[`accounts.${index}.bankName`]}
                                            onChange={(e) => patchItem("accounts", index, { bankName: e.target.value })}
                                        />
                                        <FieldError message={fieldErrors[`accounts.${index}.bankName`]} />
                                    </Form.Group>
                                </Col>
                                <Col className="mt-4">
                                    <Form.Group className="text-left">
                                        <Form.Label className="text-left text-nowrap">Account type</Form.Label>
                                        <Form.Control as="select" value={item.accountType || ""} onChange={(e) => patchItem("accounts", index, { accountType: e.target.value })}>
                                            {com.bankaccounttypes.map(([value, label]) => (
                                                <option value={value} key={value}>{label}</option>
                                            ))}
                                        </Form.Control>
                                    </Form.Group>
                                </Col>
                                <Col className="mt-4" data-field={`accounts.${index}.account`}>
                                    <Form.Group className="text-left">
                                        <Form.Label className="text-left text-nowrap">*Account number</Form.Label>
                                        <Form.Control
                                            placeholder="Last four digits"
                                            value={item.account || ""}
                                            isInvalid={!!fieldErrors[`accounts.${index}.account`]}
                                            onChange={(e) => patchItem("accounts", index, { account: e.target.value })}
                                        />
                                        <FieldError message={fieldErrors[`accounts.${index}.account`]} />
                                    </Form.Group>
                                </Col>
                                <Col className="mt-4" data-field={`accounts.${index}.balance`}>
                                    <Form.Group className="text-left">
                                        <Form.Label className="text-left">*Balance</Form.Label>
                                        <DollarInputV2
                                            value={item.balance || ""}
                                            status={fieldErrors[`accounts.${index}.balance`] ? "error" : undefined}
                                            onChange={(value) => patchItem("accounts", index, { balance: `${value || ""}` })}
                                        />
                                        <FieldError message={fieldErrors[`accounts.${index}.balance`]} />
                                    </Form.Group>
                                </Col>
                                {ownershipSelect(item, "accounts", index)}
                            </Form.Row>
                            {sharedWithRow(item, "accounts", index)}
                        </Col>
                    </Row>
                    <div className="finance-v2-card-footer">
                        {index === assets.accounts.length - 1 && <AntButton type="link" onClick={addAccount}>+ Add</AntButton>}
                        <AntButton type="link" danger onClick={() => {
                            setFieldErrors({})
                            setList("accounts", (items) => removeListItem(items, index))
                        }}>Remove</AntButton>
                    </div>
                </div>
            ))}

            <YesNoQuestion
                label="Have you received any gifts in the last 2 months, or do you expect to receive any gifts to help with obtaining this loan?"
                value={assets.gifts.length > 0}
                onChange={(value) => {
                    setFieldErrors({})
                    if (value) {
                        if (assets.gifts.length === 0) addGift()
                        return
                    }
                    setList("gifts", [])
                }}
            />
            {assets.gifts.map((item, index) => (
                <div className="finances-type incard mt-4 pt-0 finance-v2-card" style={{ padding: "1rem" }} key={item.key || index}>
                    <Row style={{ position: "relative" }}>
                        <Col>
                            <Form.Row className="finances-type-mobile">
                                <Col className="mt-4" data-field={`gifts.${index}.assetType`}>
                                    <Form.Group>
                                        <Form.Label className="text-left">*Asset type</Form.Label>
                                        <Form.Control as="select" value={item.assetType || ""} isInvalid={!!fieldErrors[`gifts.${index}.assetType`]} onChange={(e) => patchItem("gifts", index, { assetType: e.target.value })}>
                                            <option value="">Select</option>
                                            <option value="GiftOfCash">Cash Gift</option>
                                            <option value="GiftOfPropertyEquity">Gift of Equity</option>
                                            <option value="Grant">Grant</option>
                                        </Form.Control>
                                        <FieldError message={fieldErrors[`gifts.${index}.assetType`]} />
                                    </Form.Group>
                                </Col>
                                <Col className="mt-4" data-field={`gifts.${index}.deposited`}>
                                    <Form.Group>
                                        <Form.Label className="text-left">
                                            *Is deposited?
                                            <Tips
                                                placement="bottom"
                                                title="Is it already deposited in your bank account"
                                            />
                                        </Form.Label>
                                        <Form.Control as="select" value={item.deposited == null ? "" : item.deposited ? "deposited" : "notdeposited"} isInvalid={!!fieldErrors[`gifts.${index}.deposited`]} onChange={(e) => patchItem("gifts", index, { deposited: e.target.value === "deposited" })}>
                                            <option value="">Select</option>
                                            <option value="deposited">Deposited</option>
                                            <option value="notdeposited">Not Deposited</option>
                                        </Form.Control>
                                        <FieldError message={fieldErrors[`gifts.${index}.deposited`]} />
                                    </Form.Group>
                                </Col>
                                <Col className="mt-4" data-field={`gifts.${index}.source`}>
                                    <Form.Group>
                                        <Form.Label className="text-left">
                                            <GiftSourceLabel source={item.source} />
                                        </Form.Label>
                                        <Form.Control
                                            as="select"
                                            value={item.source || ""}
                                            isInvalid={!!fieldErrors[`gifts.${index}.source`]}
                                            onChange={(e) => {
                                                const source = e.target.value
                                                const srcInfo = giftSourceInfo(source)
                                                const autoDesc = srcInfo && source !== "Other" ? (srcInfo[1] || com.unCamelCase(source) || source) : ""
                                                patchItem("gifts", index, {
                                                    source,
                                                    sourceDescription: autoDesc,
                                                })
                                            }}
                                        >
                                            <option value="">Select</option>
                                            {com.fundssourcetype.map((s) => (
                                                <option value={s[0]} key={s[0]}>{s[1] || com.unCamelCase(s[0]) || s[0]}</option>
                                            ))}
                                        </Form.Control>
                                        <FieldError message={fieldErrors[`gifts.${index}.source`]} />
                                    </Form.Group>
                                </Col>
                                <Col className="mt-4" data-field={`gifts.${index}.value`}>
                                    <Form.Group>
                                        <Form.Label className="text-left">*Market value</Form.Label>
                                        <DollarInputV2
                                            value={item.value || ""}
                                            status={fieldErrors[`gifts.${index}.value`] ? "error" : undefined}
                                            onChange={(value) => patchItem("gifts", index, { value: `${value < 1 ? 1 : (value || "")}` })}
                                        />
                                        <FieldError message={fieldErrors[`gifts.${index}.value`]} />
                                    </Form.Group>
                                </Col>
                                {ownershipSelect(item, "gifts", index)}
                            </Form.Row>
                            {sharedWithRow(item, "gifts", index)}
                        </Col>
                    </Row>
                    <div className="finance-v2-card-footer">
                        {index === assets.gifts.length - 1 && <AntButton type="link" onClick={addGift}>+ Add</AntButton>}
                        <AntButton type="link" danger onClick={() => {
                            setFieldErrors({})
                            setList("gifts", (items) => removeListItem(items, index))
                        }}>Remove</AntButton>
                    </div>
                </div>
            ))}

            <YesNoQuestion
                label="Do you have any other assets or credits you would like to use to qualify for this loan?"
                value={assets.otherAssets.length > 0}
                onChange={(value) => {
                    setFieldErrors({})
                    if (value) {
                        if (assets.otherAssets.length === 0) addOtherAsset()
                        return
                    }
                    setList("otherAssets", [])
                }}
            />
            {assets.otherAssets.map((item, index) => (
                <div className="finances-type incard mt-4 pt-0 finance-v2-card" style={{ padding: "1rem" }} key={item.key || index}>
                    <Row style={{ position: "relative" }}>
                        <Col>
                            <Form.Row className="finances-type-mobile">
                                <Col className="mt-4" data-field={`otherAssets.${index}.assetType`}>
                                    <Form.Group>
                                        <Form.Label className="text-left">
                                            <OtherAssetLabel assetType={item.assetType} />
                                        </Form.Label>
                                        <Form.Control
                                            as="select"
                                            value={item.assetType || ""}
                                            isInvalid={!!fieldErrors[`otherAssets.${index}.assetType`]}
                                            onChange={(e) => {
                                                const assetType = e.target.value
                                                const selected = otherAssetType(assetType)
                                                patchItem("otherAssets", index, {
                                                    assetType,
                                                    description: assetType === "Other" ? "" : (selected?.[1] || ""),
                                                })
                                            }}
                                        >
                                            {com.otherassetsandcredits.map(([value, label]) => (
                                                <option value={value} key={value}>{label}</option>
                                            ))}
                                        </Form.Control>
                                        <FieldError message={fieldErrors[`otherAssets.${index}.assetType`]} />
                                    </Form.Group>
                                </Col>
                                {item.assetType === "Other" && (
                                    <Col className="mt-4" data-field={`otherAssets.${index}.description`}>
                                        <Form.Group>
                                            <Form.Label className="text-left">*Description</Form.Label>
                                            <Form.Control
                                                value={item.description || ""}
                                                isInvalid={!!fieldErrors[`otherAssets.${index}.description`]}
                                                onChange={(e) => patchItem("otherAssets", index, { description: e.target.value })}
                                            />
                                            <FieldError message={fieldErrors[`otherAssets.${index}.description`]} />
                                        </Form.Group>
                                    </Col>
                                )}
                                <Col className="mt-4" data-field={`otherAssets.${index}.value`}>
                                    <Form.Group>
                                        <Form.Label className="text-left">*Value</Form.Label>
                                        <DollarInputV2
                                            value={item.value || ""}
                                            status={fieldErrors[`otherAssets.${index}.value`] ? "error" : undefined}
                                            onChange={(value) => patchItem("otherAssets", index, { value: `${value < 1 ? 1 : (value || "")}` })}
                                        />
                                        <FieldError message={fieldErrors[`otherAssets.${index}.value`]} />
                                    </Form.Group>
                                </Col>
                                {ownershipSelect(item, "otherAssets", index)}
                            </Form.Row>
                            {sharedWithRow(item, "otherAssets", index)}
                        </Col>
                    </Row>
                    <div className="finance-v2-card-footer">
                        {index === assets.otherAssets.length - 1 && <AntButton type="link" onClick={addOtherAsset}>+ Add</AntButton>}
                        <AntButton type="link" danger onClick={() => {
                            setFieldErrors({})
                            setList("otherAssets", (items) => removeListItem(items, index))
                        }}>Remove</AntButton>
                    </div>
                </div>
            ))}

        </div>
        <Row className="text-left application-bottom-bar">
            <div className="d-flex align-items-center">
                <BootstrapButton
                    id="previous"
                    type="button"
                    variant="zeitro-primary"
                    disabled={saving}
                    className="operate-btn back mr-3"
                    onClick={onBack}
                >
                    Back
                </BootstrapButton>
                <BootstrapButton
                    id="next"
                    type="button"
                    variant="zeitro-primary"
                    disabled={saving}
                    className="text-center operate-btn btn"
                    onClick={handleNext}
                >
                    Next
                </BootstrapButton>
            </div>
        </Row>
        </>
    )
}

export default AssetsV2
